'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  className?: string;
}

interface Bubble {
  x: number;
  y: number;
  r: number;
  speed: number;
  opacity: number;
  drift: number;
  phase: number;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const bubblesRef = useRef<Bubble[]>([]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialise bubbles
    const count = 38;
    bubblesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      r: 1.5 + Math.random() * 3.5,
      speed: 0.012 + Math.random() * 0.022,
      opacity: 0.12 + Math.random() * 0.28,
      drift: (Math.random() - 0.5) * 0.008,
      phase: Math.random() * Math.PI * 2,
    }));

    let frame = 0;
    const draw = () => {
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width;
      const h = canvas.height;

      bubblesRef.current.forEach((b) => {
        // Rise upward
        b.y -= b.speed;
        // Gentle horizontal drift
        b.x += Math.sin(frame * 0.01 + b.phase) * b.drift;
        // Reset when off top
        if (b.y < -2) {
          b.y = 102;
          b.x = 30 + Math.random() * 50; // keep near center-right
        }

        const px = (b.x / 100) * w;
        const py = (b.y / 100) * h;

        // Bubble circle
        const grad = ctx.createRadialGradient(px - b.r * 0.3, py - b.r * 0.3, b.r * 0.1, px, py, b.r);
        grad.addColorStop(0, `rgba(200, 245, 255, ${b.opacity})`);
        grad.addColorStop(0.6, `rgba(100, 210, 230, ${b.opacity * 0.6})`);
        grad.addColorStop(1, `rgba(60, 180, 210, 0)`);

        ctx.beginPath();
        ctx.arc(px, py, b.r, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Specular highlight
        ctx.beginPath();
        ctx.arc(px - b.r * 0.35, py - b.r * 0.35, b.r * 0.28, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${b.opacity * 0.7})`;
        ctx.fill();
      });

      frame++;
      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <section className={`relative min-h-screen flex items-center overflow-hidden ${className}`}>
      {/* ── BASE IMAGE ── */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/scuba-diving-cyprus-divers-descent-zenobia-1777622476599.jpg"
          alt="Scuba divers descending into the Zenobia wreck in Cyprus, Mediterranean sea"
          className="w-full h-full object-cover object-center"
          priority
        />

        {/* ── LAYER 1: Soft teal gradient overlay — replaces dark overlay ── */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(0,60,80,0.72) 0%, rgba(0,80,100,0.50) 30%, rgba(0,100,120,0.22) 58%, rgba(0,80,100,0.18) 100%)',
          }}
        />

        {/* ── LAYER 2: Teal color-grade tint (vibrant, not dark) ── */}
        <div
          className="absolute inset-0"
          style={{
            background: 'rgba(0, 130, 160, 0.10)',
            mixBlendMode: 'screen',
          }}
        />

        {/* ── LAYER 3: Bottom haze — lighter than before ── */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgba(0,40,60,0.38) 0%, rgba(0,40,60,0.10) 20%, transparent 38%)',
          }}
        />

        {/* ── LAYER 4: Volumetric light rays from center-top ── */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 55% 65% at 58% 3%, rgba(140,250,255,0.32) 0%, transparent 65%),
              radial-gradient(ellipse 32% 72% at 54% 0%, rgba(160,250,255,0.20) 0%, transparent 75%),
              radial-gradient(ellipse 75% 50% at 58% 2%, rgba(100,220,240,0.16) 0%, transparent 60%)
            `,
          }}
        />

        {/* ── LAYER 5: Soft edge vignette — center stays bright ── */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 82% 78% at 58% 45%, transparent 42%, rgba(0,30,50,0.38) 100%)',
          }}
        />

        {/* ── LAYER 6: SVG light ray fan ── */}
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.09 }}
          preserveAspectRatio="none"
        >
          <defs>
            <radialGradient id="rayGlow" cx="58%" cy="4%" r="70%" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#7EEEFF" stopOpacity="1" />
              <stop offset="100%" stopColor="#7EEEFF" stopOpacity="0" />
            </radialGradient>
          </defs>
          {[
            'M 58% 4% L 10% 100% L 18% 100%',
            'M 58% 4% L 22% 100% L 30% 100%',
            'M 58% 4% L 35% 100% L 42% 100%',
            'M 58% 4% L 48% 100% L 54% 100%',
            'M 58% 4% L 60% 100% L 66% 100%',
            'M 58% 4% L 72% 100% L 78% 100%',
            'M 58% 4% L 84% 100% L 90% 100%',
          ].map((d, i) => (
            <path key={i} d={d} fill="url(#rayGlow)" opacity={0.6 - i * 0.06} />
          ))}
        </svg>

        {/* ── LAYER 7: Faint octopus silhouette (right side, 10% opacity) ── */}
        <div
          className="absolute"
          style={{
            right: '2%',
            top: '8%',
            width: '48%',
            height: '82%',
            opacity: 0.10,
            pointerEvents: 'none',
          }}
        >
          <svg
            viewBox="0 0 400 500"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <linearGradient id="octGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00C8E0" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#006880" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {/* Mantle */}
            <ellipse cx="200" cy="155" rx="72" ry="90" fill="none" stroke="url(#octGrad)" strokeWidth="2.5" />
            {/* Eyes */}
            <circle cx="174" cy="138" r="11" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <circle cx="226" cy="138" r="11" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            {/* 8 tentacles — thin outlines */}
            <path d="M158,238 Q118,318 78,418 Q68,448 88,458 Q108,468 118,438 Q138,378 168,298 Q178,268 173,248" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M173,243 Q148,328 128,428 Q123,458 143,463 Q163,466 166,436 Q176,358 188,268 Q191,253 183,246" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M188,246 Q183,338 178,443 Q178,468 198,470 Q218,470 218,443 Q213,338 208,246" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M208,246 Q213,328 228,428 Q231,458 251,456 Q270,453 266,423 Q253,353 233,266 Q226,250 216,246" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M223,248 Q253,328 278,428 Q286,456 306,450 Q324,443 316,416 Q296,358 260,276 Q248,256 233,250" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M236,253 Q283,318 323,408 Q336,436 354,426 Q370,414 358,390 Q326,338 276,266 Q260,246 242,250" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M246,260 Q308,308 358,383 Q376,406 390,393 Q402,378 388,358 Q346,306 282,270 Q264,258 250,258" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
            <path d="M143,240 Q93,298 48,368 Q34,390 48,406 Q62,420 78,404 Q116,358 160,278 Q168,260 156,246" fill="none" stroke="url(#octGrad)" strokeWidth="2" />
          </svg>
        </div>

        {/* ── LAYER 8: Faint diver silhouette (upper-right, 12% opacity) ── */}
        <div
          className="absolute"
          style={{
            right: '8%',
            top: '5%',
            width: '18%',
            height: '35%',
            opacity: 0.12,
            pointerEvents: 'none',
          }}
        >
          <svg
            viewBox="0 0 120 200"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <linearGradient id="diverGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#80E8F8" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#20A8C0" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            {/* Head / mask */}
            <ellipse cx="60" cy="22" rx="14" ry="16" fill="none" stroke="url(#diverGrad)" strokeWidth="2" />
            {/* Tank */}
            <rect x="50" y="40" width="20" height="38" rx="6" fill="none" stroke="url(#diverGrad)" strokeWidth="2" />
            {/* Body */}
            <rect x="44" y="42" width="32" height="36" rx="8" fill="none" stroke="url(#diverGrad)" strokeWidth="2" />
            {/* Arms */}
            <path d="M44,52 Q28,58 20,70" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            <path d="M76,52 Q92,58 100,70" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            {/* Legs */}
            <path d="M52,78 Q48,110 44,140" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            <path d="M68,78 Q72,110 76,140" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            {/* Fins */}
            <path d="M44,140 Q30,148 18,155 Q28,162 44,155" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            <path d="M76,140 Q90,148 102,155 Q92,162 76,155" fill="none" stroke="url(#diverGrad)" strokeWidth="2" strokeLinecap="round" />
            {/* Bubbles from regulator */}
            <circle cx="46" cy="30" r="3" fill="none" stroke="url(#diverGrad)" strokeWidth="1.5" />
            <circle cx="40" cy="22" r="2.5" fill="none" stroke="url(#diverGrad)" strokeWidth="1.5" />
            <circle cx="36" cy="14" r="2" fill="none" stroke="url(#diverGrad)" strokeWidth="1.5" />
            <circle cx="33" cy="7" r="1.5" fill="none" stroke="url(#diverGrad)" strokeWidth="1.2" />
          </svg>
        </div>

        {/* ── LAYER 9: Animated bubble canvas ── */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        />
      </div>

      {/* ── CONTENT — left aligned ── */}
      <div className="relative z-10 w-full container mx-auto px-4 sm:px-6 py-20 sm:py-28 md:py-36">
        <div className="max-w-2xl">

          {/* H1 */}
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-headline font-bold mb-4 sm:mb-5 tracking-tight leading-tight"
            style={{
              color: '#EAF8FC',
              textShadow: '0 2px 16px rgba(0,60,80,0.45)',
            }}
          >
            Scuba Diving in Cyprus
          </h1>

          {/* Subheading */}
          <p
            className="text-base sm:text-lg md:text-xl font-normal max-w-xl mb-8 sm:mb-10 leading-relaxed"
            style={{
              color: '#C8E8F2',
              lineHeight: '1.75',
              textShadow: '0 1px 8px rgba(0,40,60,0.35)',
            }}
          >
            Discover where to dive, what you&apos;ll actually see, and what conditions are really like — before you go.
            From the Zenobia wreck to reefs, caves, and hidden spots across the island.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Primary */}
            <button
              onClick={() => scrollToSection('dive-sites-section')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #3DD8E8 0%, #1ABCCC 100%)',
                color: '#062830',
                border: '1.5px solid #3DD8E8',
                boxShadow: '0 0 0 0 rgba(61,216,232,0)',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'translateY(-2px)';
                el.style.boxShadow = '0 6px 24px rgba(61,216,232,0.45)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'translateY(0)';
                el.style.boxShadow = '0 0 0 0 rgba(61,216,232,0)';
              }}
            >
              Find Your Next Dive →
            </button>

            {/* Secondary */}
            <Link
              href="/marine-life"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base transition-all duration-200"
              style={{
                background: 'rgba(61,216,232,0.08)',
                color: '#7EEEFF',
                border: '1.5px solid rgba(61,216,232,0.55)',
                boxShadow: '0 0 0 0 rgba(61,216,232,0)',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'translateY(-2px)';
                el.style.background = 'rgba(61,216,232,0.16)';
                el.style.boxShadow = '0 6px 20px rgba(61,216,232,0.30)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'translateY(0)';
                el.style.background = 'rgba(61,216,232,0.08)';
                el.style.boxShadow = '0 0 0 0 rgba(61,216,232,0)';
              }}
            >
              What You&apos;ll See Underwater →
            </Link>
          </div>

          {/* Micro-bridge */}
          <p
            className="mt-5 text-sm"
            style={{ color: 'rgba(200,232,242,0.65)' }}
          >
            Start with the best dives — or explore what&apos;s waiting underwater.
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <Icon name="ChevronDownIcon" size={28} style={{ color: 'rgba(111,211,224,0.6)' }} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;